import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {MatSort} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {MatTableDataSource} from '@angular/material/table';
import {MatDialog} from '@angular/material/dialog';
import {forkJoin} from 'rxjs';
import {take} from 'rxjs/operators';
import {KeycloakService} from 'keycloak-angular';
import {DateUtil} from '../../../../../common/util/DateUtil';
import {CaseHospitalDTO} from '../../../../models/case-hospital/CaseHospitalDTO';
import {ClmRequestBuilder} from 'src/app/modules/clm/services/clm-request-builder';
import {IssueTicketCaseLinkDTO} from '../../../../models/IssueTicketCaseLinkDTO';
import {IssueTicketDTO} from '../../../../models/IssueTicketDTO';
import {SharedService} from '../../../../../common/services/shared-service';
import {AddIssueTicketDialogComponent} from '../../../dialogs/add-issue-ticket-dialog/add-issue-ticket-dialog.component';
import {ConfirmationDialogComponent} from '../../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {IssueTicketAttachmentsLinkDTO} from '../../../../models/IssueTicketAttachmentsLinkDTO';
import {FileUploadService} from '../../../../../common/services/file-upload.service';
import {CaseFuneralDTO} from '../../../../models/CaseFuneralDTO';
import {Role} from '../../../../types/role.enum';
import {CaseService} from '../../../../services/case.service';

enum TableColumns {
  ICONS = 'icons',
  ISSUE_TICKET_ID = 'issueTicketId',
  NAME = 'name',
  REPORTER = 'reporter',
  ASSIGNEE = 'assignee',
  STATUS = 'status',
  RESOLUTION = 'resolution',
  PRIORITY = 'priority',
  MODULE = 'module',
  START_DATE = 'startDate',
  DUE_DATE = 'dueDate',
  END_DATE = 'endDate',
}

@Component({
  selector: 'app-case-issues-step',
  templateUrl: './case-issues-step.component.html',
  styleUrls: ['../../../../../../../styles/global/_add-view-edit-styles.scss']
})
export class CaseIssuesStepComponent implements OnChanges, OnInit {
  readonly dateFormat = DateUtil.dateFormat;
  readonly tableColumns = TableColumns;
  readonly paginatorControls = ClmRequestBuilder.getTableControls();

  @Input() caseId: any;
  @Input() type: string;
  @Input() loggedInUserName: string;
  @Input() data: CaseFuneralDTO|CaseHospitalDTO;
  @Input() formGroup: FormGroup;
  @Input() isEditMode: boolean;

  @Output() updateForm: EventEmitter<FormGroup> = new EventEmitter();

  issueTicketCasesDisplayedColumns: string[] = Object.values(TableColumns);
  issueTicketCaseLinkDataSource: MatTableDataSource<IssueTicketCaseLinkDTO> = new MatTableDataSource();
  selectedIssueTicketCase: IssueTicketDTO;

  private userRoles: string[];

  constructor(private dialog: MatDialog,
              private caseService: CaseService,
              private fileUploadService: FileUploadService,
              private keycloakService: KeycloakService,
              private sharedService: SharedService) {
  }

  @ViewChild('MatSortIssueStep') set matSortIssueStep(ms: MatSort) {
    if (!ms) {
      return;
    }
    this.issueTicketCaseLinkDataSource.sortingDataAccessor = (item: IssueTicketCaseLinkDTO, property: string) => {
      const {issueTicketDTO} = item;
      if (typeof issueTicketDTO[property] === 'object' && issueTicketDTO[property] !== null) {
        return issueTicketDTO[property].name; // e.g `provider` property
      }
      else {
        return issueTicketDTO[property];
      }
    };
    this.issueTicketCaseLinkDataSource.sort = ms;
  }

  @ViewChild('MatPaginatorIssueStep') set matPaginatorIssueStep(mp: MatPaginator) {
    if (!mp) {
      return;
    }
    this.issueTicketCaseLinkDataSource.paginator = mp;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isEditMode) {
      this.isEditMode = changes.isEditMode.currentValue;
    }
    if (changes.formGroup) {
      this.formGroup = changes.formGroup.currentValue;
    }
    if (changes.data && changes.data.currentValue) {
      this.data = changes.data.currentValue;
      this.setFormsForEdit(this.data);
    }
  }

  ngOnInit(): void {
    // Notify Updated Reference of FormGroup to parent:
    this.updateForm.next(this.formGroup);
    this.userRoles = this.keycloakService.getUserRoles();
  }

  editIssueTicket(issueTicketCase?: IssueTicketCaseLinkDTO): void {
    this.selectedIssueTicketCase = issueTicketCase ? issueTicketCase.issueTicketDTO : null;
    const dialogRef = this.dialog.open(AddIssueTicketDialogComponent, {
      width: '60vw',
      height: '85vh',
      data: {
        isEditMode: !!issueTicketCase, // Note Add -> issueTicketCase: null, Edit -> issueTicketCase: row
        issueTicketCase: this.selectedIssueTicketCase,
        formGroup: this.formGroup
      },
      disableClose: true
    });

    let isIssueInEditMode = false;

    dialogRef
      .afterClosed()
      .subscribe((result: any) => {
        const {issueTicketCasePayload, formGroup} = result;
        isIssueInEditMode = result?.isEditMode;
        this.formGroup = formGroup;

        if (issueTicketCasePayload) {
          // Edit Case
          if (this.isEditMode && this.caseId) {
            if (!isIssueInEditMode) {
              this.handleAddNewIssueWithoutCaseSave(true, issueTicketCasePayload);
            }
            else {
              this.handleAddNewIssueWithoutCaseSave(false, issueTicketCasePayload);
            }
          } // Add Case
          else if (!this.isEditMode && result) {
            if (!isIssueInEditMode) {
              this.issueTicketCaseLinkDataSource.data.push(issueTicketCasePayload);
              this.issueTicketCaseLinkDataSource._updateChangeSubscription();
            }
            else {
              this.onUpdateIssueTicketCase(issueTicketCasePayload);
            }
          }
        }

      });
  }

  handleAddNewIssueWithoutCaseSave(addNewIssue: boolean, issueTicketCasePayload: IssueTicketCaseLinkDTO): void {
    this.caseService.createTicket({
      caseId: Number(this.caseId),
      issueTicketDTO: issueTicketCasePayload?.issueTicketDTO,
      created: this.isEditMode ? issueTicketCasePayload.created : new Date(),
      createdBy: this.isEditMode ? issueTicketCasePayload.createdBy : this.loggedInUserName,
      updated: this.isEditMode ? new Date() : null,
      updatedBy: this.isEditMode ? this.loggedInUserName : null,
      tenantId: null
    })
      .pipe(take(1))
      .subscribe({
        next: (newIssueTicketCaseLinkDTO: IssueTicketCaseLinkDTO) => {
          if (addNewIssue) {
            this.issueTicketCaseLinkDataSource.data.push(newIssueTicketCaseLinkDTO);
            this.issueTicketCaseLinkDataSource._updateChangeSubscription();
          }
          this.onUpdateIssueTicketCase(newIssueTicketCaseLinkDTO);
        },
        error: (error) => {
          this.sharedService.openErrorDialog(error.businessMessage || 'Error occurred while saving Issue Ticket');
        }
      });
  }

  onUpdateIssueTicketCase(issueTicketCase: IssueTicketCaseLinkDTO): void {
    const {issueTicketId} = issueTicketCase && issueTicketCase.issueTicketDTO;
    const index = this.issueTicketCaseLinkDataSource
                      .data
                      .findIndex((item: IssueTicketCaseLinkDTO) => {
                        if (issueTicketId === null) {
                          // Note: (Hotfix)
                          // In Case of Add we are not adding `issueTicketId` which is null so in case of edit of newly entry case
                          // we check both name and issueTicketId
                          return item.issueTicketDTO.issueTicketId === issueTicketId;
                        }
                        else {
                          return item.issueTicketDTO.issueTicketId === issueTicketId;
                        }
                      });

    if (index > -1) {
      this.issueTicketCaseLinkDataSource.data[index] = issueTicketCase;
      this.issueTicketCaseLinkDataSource._updateChangeSubscription();
    }
    else {
      this.sharedService.openSnackBar('No Matching row found to update data', 'Dismiss');
    }
  }

  getPayload(): { issueTicketCases: IssueTicketCaseLinkDTO[] } {
    return {
      issueTicketCases: this.issueTicketCaseLinkDataSource.data && this.issueTicketCaseLinkDataSource.data.length > 0 ? this.issueTicketCaseLinkDataSource.data : null
    };
  }

  deleteIssueTicketCaseConfirmation(event: MouseEvent, issueTicketCase: IssueTicketCaseLinkDTO): void {
    const {name, issueTicketId, issueTicketAttachmentsLink} = issueTicketCase && issueTicketCase.issueTicketDTO;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: `Are you sure you wish to delete Issue ${name} ticket?`
    });

    dialogRef.afterClosed()
      .subscribe({
        next: (isConfirmed) => {
          if (isConfirmed) {
            const issueTicketAttachmentsToDelete = [];

            // Note:
            // Case Add: Delete files in case of "Add" only as BE fails to delete "Referenced" file
            // Case Edit: Delete files in case of "Edit" when new TicketCase is added as `issueTicketId == null`;

            if ((!this.isEditMode || issueTicketId == null) && issueTicketAttachmentsLink?.length > 0) {
              issueTicketAttachmentsLink
                .forEach((issueTicketAttachment: IssueTicketAttachmentsLinkDTO) => {
                  issueTicketAttachmentsToDelete.push(this.fileUploadService.deleteFile(issueTicketAttachment.issueTicketAttachment.fileId));
                });

              forkJoin(issueTicketAttachmentsToDelete)
                .pipe(take(1))
                .subscribe(
                  () => {
                    this.deleteIssueTicketCaseConfirmed(issueTicketCase);
                  }, (errorResponse) => {
                    const error = errorResponse.error;
                    this.sharedService.openErrorDialog(error.businessMessage);
                  });

            }
            else {
              if (this.caseId && this.isEditMode && issueTicketId != null) {
                this.caseService.deleteTicket(this.caseId, issueTicketId)
                  .pipe(take(1))
                  .subscribe({
                    next: () => {
                      this.deleteIssueTicketCaseConfirmed(issueTicketCase);
                    },
                    error: (error) => {
                      this.sharedService.openErrorDialog(error.businessMessage || 'Error occurred while deleting Issue Ticket');
                    }
                  });
              }
              else {
                this.deleteIssueTicketCaseConfirmed(issueTicketCase);
              }
            }
          }
        }
      });
  }

  isAuthorizedToDeleteIssue(): boolean {
    return this.userRoles.some((role: string) => (
      role === Role.FinancialTeamLeader || role === Role.ClaimsTeamLeader
    ));
  }

  private deleteIssueTicketCaseConfirmed(issueTicketCase: IssueTicketCaseLinkDTO):void {
    const {name, issueTicketId} = issueTicketCase && issueTicketCase.issueTicketDTO;

    const index = this.issueTicketCaseLinkDataSource
                      .data
                      .findIndex((item: IssueTicketCaseLinkDTO) => {
                        // Note
                        // Case Add: In Case of Add we are not adding `issueTicketId` -> `null`
                        // Case Delete: (Already existed) Check both name and issueTicketId
                        return item.issueTicketDTO.issueTicketId === issueTicketId && item.issueTicketDTO.name === name;
                      });
    if (index > -1) {
      this.issueTicketCaseLinkDataSource.data.splice(index, 1);
      this.issueTicketCaseLinkDataSource._updateChangeSubscription();
    }
    else {
      this.sharedService.openSnackBar('No matching record found', 'Dismiss');
    }
  }

  private setFormsForEdit(data: CaseFuneralDTO|CaseHospitalDTO): void {
    const {issueTicketCases} = data;
    if (issueTicketCases && issueTicketCases.length > 0) {
      this.issueTicketCaseLinkDataSource.data = issueTicketCases; // Assign the data to the data source for the table to render
    }
    else {
      this.issueTicketCaseLinkDataSource.data = [];
    }
  }
}
